import { createSlice } from "@reduxjs/toolkit";
import { REQUEST_STATUS } from "lib/types";

import {
  checkSuccessfulAuthorisationAction,
  createFlickSignUpAction,
  createWindcaveTransactionAction,
  fetchAvailableProductsAction,
  fetchBillingEstimateAction,
  fetchProductDataAction,
  getFlickSignUpAction,
  updateFlickSignUpAction,
  validateAddressAction,
} from "./actions";
import { CommercialProduct, CoreState, Product } from "./types";

// const DEFAULT_PRODUCTS = ["flat", "off_peak", "freestyle"] as Product[]
// Freestyle is currently disabled, revert defaults to the above when it is available again.
export const DEFAULT_PRODUCTS = ["flat", "off_peak"] as Product[];
export const COMMERCIAL_PRODUCTS = ["business"] as CommercialProduct[];

// The code for INITIAL_STATE is omitted for brevity

const INITIAL_STATE: CoreState = {
  validateAddressRequestStatus: REQUEST_STATUS.Idle,
  createFlickSignUpRequestStatus: REQUEST_STATUS.Idle,
  updateFlickSignUpRequestStatus: REQUEST_STATUS.Idle,
  flickSignUpRequestStatus: REQUEST_STATUS.Idle,
  windcaveTransactionRequestStatus: REQUEST_STATUS.Idle,
  windcaveAuthorisationRequestStatus: REQUEST_STATUS.Idle,
  billingEstimateRequestStatus: REQUEST_STATUS.Idle,
  productDataRequestStatus: REQUEST_STATUS.Idle,
  eligibleAddresses: {
    data: [],
    included: [],
  },
  flickUserId: null,
  user: {
    checks: {
      marketingAccepted: false,
      smsSwitchNotificationsAccepted: false,
      termsAccepted: false,
      creditCheckAccepted: false,
      isCommercialAccount: undefined,
    },
    details: {
      emailAddress: "",
      firstName: "",
      lastName: "",
      prefName: "",
      mobileNumber: "",
      homeNumber: "",
      companyName: "",
      product: undefined,
    },
    payment: {
      paymentType: undefined,
      billingPeriod: undefined,
    },
    property: {
      usageType: undefined,
      medicallyDependent: undefined,
      situation: undefined,
      moveDate: undefined,
      switchDate: undefined,
    },
  },
  estimatedUsageType: "low",
  signUpState: "0",
  currentFormStep: "/join",
  partialSignUpDialogOpen: false,
  promoCode: undefined,
  leadSource: undefined,
  availableProducts: DEFAULT_PRODUCTS,
  product: undefined,
  selectedAddress: undefined,
};

// Generate actions and reducers using createSlice
const coreStateSlice = createSlice({
  name: "coreState",
  initialState: INITIAL_STATE,
  reducers: {
    // User
    setUserDetails: (state, action) => {
      state.user.details = action.payload;
    },
    setUserProperty: (state, action) => {
      state.user.property = action.payload;
    },
    setUserChecks: (state, action) => {
      state.user.checks = action.payload;
    },
    setUserPayment: (state, action) => {
      state.user.payment = action.payload;
    },
    setUserBillingPeriod: (state, action) => {
      state.user.payment.billingPeriod = action.payload;
    },
    setUserProduct: (state, action) => {
      state.user.details.product = action.payload;
    },
    setCurrentFormStep: (state, action) => {
      state.currentFormStep = action.payload;
    },
    setPartialSignUpDialogState: (state, action) => {
      state.partialSignUpDialogOpen = action.payload;
    },
    setPromoCode: (state, action) => {
      state.promoCode = action.payload;
    },
    setAvailableProducts: (state, action) => {
      state.availableProducts = action.payload;
    },
    selectAddress: (state, action) => {
      state.validateAddressRequestStatus = REQUEST_STATUS.Fulfilled;
      state.selectedAddress = action.payload.address;
      state.selectedEligibilityType = action.payload.eligibility;
    },
    updateEligibility: (state, action) => {
      state.selectedEligibilityType = action.payload.eligibility;
      state.billingEstimateRequestStatus = REQUEST_STATUS.Fulfilled;
    },
    clearAddress: (state) => {
      state.billingEstimateRequestStatus = REQUEST_STATUS.Idle;
    },
    setFlickUserId: (state, action) => {
      state.flickUserId = action.payload;
    },
    setFlickSignUpStateComplete: (state) => {
      state.signUpState = "complete";
    },
    setCreationSourceIdentifier: (state) => {
      state.creationSource = "sign_up_api_guided";
    },
    setLeadSource: (state, action) => {
      state.leadSource = action.payload;
    },
    setSignUpPaymentType: (state, action) => {
      state.signUpPaymentType = action.payload;
    },
    setAddress: (state, action) => {
      state.selectedAddress = action.payload;
    },
    clearState: (state) => {
      // set state as Initial state
      Object.keys(INITIAL_STATE).forEach((key) => {
        state[key] = INITIAL_STATE[key];
      });
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(fetchProductDataAction.request, (state) => {
        state.productDataRequestStatus = REQUEST_STATUS.Pending;
      })
      .addCase(fetchProductDataAction.success, (state, action) => {
        state.productDataRequestStatus = REQUEST_STATUS.Fulfilled;
        state.product = action.payload;
      })
      .addCase(fetchProductDataAction.failure, (state) => {
        state.productDataRequestStatus = REQUEST_STATUS.Failed;
      })
      .addCase(validateAddressAction.request, (state) => {
        state.validateAddressRequestStatus = REQUEST_STATUS.Pending;
        state.selectedAddress = undefined;
      })
      .addCase(validateAddressAction.success, (state, action) => {
        state.validateAddressRequestStatus = REQUEST_STATUS.Fulfilled;
        state.eligibleAddresses = action.payload;
      })
      .addCase(validateAddressAction.failure, (state) => {
        state.validateAddressRequestStatus = REQUEST_STATUS.Failed;
      })
      .addCase(fetchAvailableProductsAction.request, (state) => {
        state.availableProducts = [];
      })
      .addCase(fetchAvailableProductsAction.success, (state, action) => {
        state.validateAddressRequestStatus = REQUEST_STATUS.Fulfilled;
        state.availableProducts =
          action.payload.data?.map((plan) => plan.attributes.product_name) ??
          [];
      })
      .addCase(fetchAvailableProductsAction.failure, (state) => {
        state.availableProducts = DEFAULT_PRODUCTS;
      })
      .addCase(fetchBillingEstimateAction.request, (state) => {
        state.billingEstimateRequestStatus = REQUEST_STATUS.Pending;
      })
      .addCase(fetchBillingEstimateAction.success, (state, action) => {
        state.billingEstimateRequestStatus = REQUEST_STATUS.Fulfilled;
        state.billEstimates = action.payload;
        state.estimatedUsageType = action.payload.data[0].attributes.usage_type;
        state.availableProducts = action.payload.data
          ? action.payload.data?.map((plan) => plan.attributes.product_name)
          : [];
      })
      .addCase(fetchBillingEstimateAction.failure, (state) => {
        state.billingEstimateRequestStatus = REQUEST_STATUS.Failed;
        state.billEstimates = { data: [] };
        state.availableProducts = DEFAULT_PRODUCTS;
      })
      .addCase(createWindcaveTransactionAction.request, (state) => {
        state.windcaveTransactionRequestStatus = REQUEST_STATUS.Pending;
      })
      .addCase(createWindcaveTransactionAction.success, (state, action) => {
        state.windcaveTransactionRequestStatus = REQUEST_STATUS.Fulfilled;
        state.windcaveSignUpId = action.payload.data.attributes.sign_up_id;
        state.transactionId = action.payload.data.id;
      })
      .addCase(createWindcaveTransactionAction.failure, (state) => {
        state.windcaveTransactionRequestStatus = REQUEST_STATUS.Failed;
      })
      .addCase(checkSuccessfulAuthorisationAction.request, (state) => {
        state.windcaveAuthorisationRequestStatus = REQUEST_STATUS.Pending;
      })
      .addCase(checkSuccessfulAuthorisationAction.success, (state, action) => {
        state.windcaveAuthorisationRequestStatus = REQUEST_STATUS.Fulfilled;
        state.windcaveSignUpId = action.payload.data.attributes.sign_up_id;
      })
      .addCase(checkSuccessfulAuthorisationAction.failure, (state) => {
        state.windcaveAuthorisationRequestStatus = REQUEST_STATUS.Failed;
      })
      .addCase(createFlickSignUpAction.request, (state) => {
        state.createFlickSignUpRequestStatus = REQUEST_STATUS.Pending;
        state.flickUserId = undefined;
      })
      .addCase(createFlickSignUpAction.success, (state, action) => {
        state.createFlickSignUpRequestStatus = REQUEST_STATUS.Fulfilled;
        state.flickUserId = action.payload.data.id;
      })
      .addCase(createFlickSignUpAction.failure, (state, action) => {
        state.createFlickSignUpRequestStatus = REQUEST_STATUS.Failed;
        state.flickAccountError = action.payload;
      })
      .addCase(updateFlickSignUpAction.request, (state) => {
        state.updateFlickSignUpRequestStatus = REQUEST_STATUS.Pending;
        state.flickAccountError = undefined;
      })
      .addCase(updateFlickSignUpAction.success, (state) => {
        state.updateFlickSignUpRequestStatus = REQUEST_STATUS.Fulfilled;
      })
      .addCase(updateFlickSignUpAction.failure, (state, action) => {
        state.updateFlickSignUpRequestStatus = REQUEST_STATUS.Failed;
        state.flickAccountError = action.payload;
      })
      .addCase(getFlickSignUpAction.request, (state) => {
        state.flickSignUpRequestStatus = REQUEST_STATUS.Pending;
        state.flickAccountError = undefined;
      })
      .addCase(getFlickSignUpAction.success, (state) => {
        state.flickSignUpRequestStatus = REQUEST_STATUS.Fulfilled;
      })
      .addCase(getFlickSignUpAction.failure, (state, action) => {
        state.flickSignUpRequestStatus = REQUEST_STATUS.Failed;
        state.flickAccountError = action.payload;
      }),
});

export const {
  setUserDetails,
  setUserProperty,
  setAvailableProducts,
  setCurrentFormStep,
  setPartialSignUpDialogState,
  setPromoCode,
  setUserBillingPeriod,
  setUserChecks,
  setUserPayment,
  setUserProduct,
  selectAddress,
  updateEligibility,
  clearAddress,
  setFlickUserId,
  setFlickSignUpStateComplete,
  setCreationSourceIdentifier,
  setLeadSource,
  setSignUpPaymentType,
  setAddress,
  clearState,
} = coreStateSlice.actions;
export const coreReducer = coreStateSlice.reducer;
